import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import { Box, Grid, Tooltip, Typography, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';

import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../../../atomic/molecules';
import { ScheduleCard } from './components';
import { SCUIButton } from '../../../../atomic/atoms';

import { useScheduleService } from '../../context/ScheduleServiceContext';
import { ScheduleServiceQuery } from '../../../../services/query';
import TimeHelper from '../../../../utils/time';
import { ResponseModal } from './components/ResponseModal/ResponseModal';
import { SD, sdCls } from './scheduleDetail.classes';

export const ScheduleDetail = () => {
    const [pageSize, setPageSize] = useState<number>(10);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [rowCount, setRowCount] = useState<number>(0);
    const [gridData, setGridData] = useState<any[]>([]);
    const [responseData, setResponseData] = useState<any>();
    const [isResponseModalOpen, setIsResponseModalOpen] = useState<boolean>(false);

    const { setSelectedJob, selectedJob, setIsLoading, refreshHistory, setRefreshHistory } = useScheduleService();
    const { t } = useTranslation();

    const getResultIcons = (isSuccess: boolean) =>
        isSuccess ? (
            <Tooltip className={SD.cellSuccessTooltip} title="Success">
                <CheckCircleIcon className={SD.cellSuccessIconSuccess} sx={{ fontSize: '24px' }} color="success" />
            </Tooltip>
        ) : (
            <Tooltip className={SD.cellSuccessTooltip} title="Failed">
                <CancelIcon className={SD.cellSuccessIconError} sx={{ fontSize: '24px' }} color="error" />
            </Tooltip>
        );

    const getGridData = (currentPageSize: number, currentPage: number) => {
        setPageSize(currentPageSize);
        setPageNumber(currentPage);

        setIsLoading(true);

        ScheduleServiceQuery.GetScheduleHistory({
            jobId: selectedJob?.jobId,
            take: currentPageSize,
            page: currentPage + 1,
        })
            .then((res: any) => {
                if (res) {
                    setGridData(res.data);
                    setRefreshHistory(false);
                    setRowCount(res.count);
                    setIsLoading(false);
                }
            })
            .catch((error: any) => {
                setRefreshHistory(false);
                Swal.fire(error.message || t('general.anErrorOccured'), '', 'error');
                setIsLoading(false);
            });
    };

    const clearData = () => {
        setGridData([]);
        setSelectedJob({});
    };

    const openResponse = (row: any) => {
        setIsResponseModalOpen(true);
        setResponseData(row);
    };

    const calculateTimeDifference = (start: string, end: string) => {
        const startTime = new Date(start).getTime();
        const endTime = new Date(end).getTime();

        const diff = endTime - startTime;

        if (diff < 0) return '-';

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        const milliseconds = diff % 1000;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}:${milliseconds.toString().padStart(3, '0')}`;
    };

    const columnData = [
        {
            field: 'backgroundJobId',
            headerName: t('scheduleService.historyId'),
            renderCell: (params: any) => {
                const handleClick = async (event: React.MouseEvent) => {
                    event.stopPropagation();
                    if (params?.value) {
                        try {
                            await navigator.clipboard.writeText(params.value);
                            Swal.fire({
                                toast: true,
                                position: 'top-end',
                                icon: 'info',
                                title: t('general.copied'),
                                showConfirmButton: false,
                                timer: 1000,
                                timerProgressBar: true,
                            });
                        } catch {
                            console.error('Copy failed:');
                        }
                    }
                };

                return (
                    <span className={SD.cellHistoryIdWrap}>
                        <Tooltip className={SD.cellHistoryIdTooltip} title={params.value || ''} arrow>
                            <Typography
                                className={SD.cellHistoryIdText}
                                onClick={handleClick}
                                noWrap
                                sx={{
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    width: '100%',
                                    cursor: 'pointer',
                                }}
                            >
                                {params.value || '-'}
                            </Typography>
                        </Tooltip>
                    </span>
                );
            },
        },
        {
            field: 'startTime',
            headerName: t('scheduleService.startTime'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SD.cellStartTime}>{TimeHelper.formatDateHourSecond(params.row?.startTime)}</span>
            ),
        },
        {
            field: 'endTime',
            headerName: t('scheduleService.endTime'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SD.cellEndTime}>{TimeHelper.formatDateHourSecond(params.row?.endTime)}</span>
            ),
        },
        {
            field: 'workTime',
            headerName: t('scheduleService.workTime'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SD.cellWorkTime}>
                    {calculateTimeDifference(params.row?.startTime, params.row?.endTime)}
                </span>
            ),
        },
        {
            field: 'errorMessage',
            headerName: t('scheduleService.errorMessage'),
            flex: 1,
            renderCell: (params: any) => <span className={SD.cellErrorMessage}>{params.value ?? ''}</span>,
        },
        {
            field: 'isSuccess',
            headerName: t('scheduleService.isSuccess'),
            renderCell: (params: any) => <span className={SD.cellSuccessIconWrap}>{getResultIcons(params.value)}</span>,
        },
        {
            field: 'showResponse',
            headerName: t('scheduleService.seeResponse'),
            flex: 1,
            renderCell: (params: any) => (
                <SCUIButton
                    className={SD.cellSeeResponseButton}
                    onClick={() => openResponse(params.row)}
                    type="primary"
                    sizeType="fixed"
                    size="s"
                    text={t('scheduleService.seeResponse')}
                />
            ),
        },
    ];

    useEffect(() => {
        if (refreshHistory) {
            getGridData(pageSize, pageNumber);
        }
    }, [refreshHistory]);

    useEffect(() => {
        getGridData(pageSize, pageNumber);
    }, []);

    return (
        <Box className={SD.page}>
            <Grid className={SD.breadcrumbRow} container direction="row" spacing={2}>
                <SCUIBreadCrumb
                    onlyTitle={t('scheduleService.detail')}
                    components={[
                        {
                            title: t('scheduleService.header'),
                            link: '/schedule-service',
                            onClick: clearData,
                        },
                    ]}
                    onClick={clearData}
                />
            </Grid>
            <Box
                className={SD.layout}
                sx={{ display: 'flex', flex: 1, flexDirection: 'row', marginTop: '1rem', alignItems: 'start' }}
            >
                <Box
                    className={SD.leftCol}
                    sx={{
                        display: 'flex',
                        marginRight: '24px',
                    }}
                >
                    <ScheduleCard />
                </Box>
                <Box
                    className={sdCls(SD.rightCol, SD.historyCard)}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ffffff, #f7f7f7)',
                        padding: '16px',
                        overflow: 'hidden',
                    }}
                >
                    <Typography
                        className={SD.historyTitle}
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '18px',
                            color: '#333',
                            marginBottom: '16px',
                        }}
                    >
                        {t('scheduleService.scheduleHistory')}
                    </Typography>
                    <Box
                        className={SD.historyActions}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            width: '100%',
                        }}
                    >
                        <Button
                            className={SD.refreshButton}
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon className={SD.refreshIcon} />}
                            onClick={() => setRefreshHistory(true)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                marginBottom: '4px',
                                padding: '8px !important',
                                textTransform: 'capitalize',
                                backgroundColor: '#03a9f4',
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: '#0288d1',
                                },
                            }}
                        >
                            {t('scheduleService.refreshHistory')}
                        </Button>
                        <Box className={SD.gridWrap} sx={{ width: '100%' }}>
                            <SCUICustomDataGrid
                                onPageSizeChange={(i: number) => {
                                    getGridData(i, 0);
                                }}
                                pageSize={pageSize}
                                paginationModeChange
                                onPageChange={(i: number) => {
                                    getGridData(pageSize, i);
                                }}
                                page={pageNumber}
                                rows={gridData}
                                columns={columnData}
                                randomId
                                rowCount={rowCount}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
            <ResponseModal
                isOpen={isResponseModalOpen}
                setIsOpen={setIsResponseModalOpen}
                responseData={responseData}
                setResponseData={setResponseData}
            />
        </Box>
    );
};
